import {
  calculateCommunityMrrCents,
  type CommunityMrrPricing,
} from '../commerce/community-membership-billing';

export type CommunityRevenueMembershipSnapshot = {
  joinedAt: Date | string;
  leftAt?: Date | string | null;
  status?: string | null;
  stripeSubscriptionId?: string | null;
  billingInterval?: string | null;
  stripeBillingStatus?: string | null;
  subscriptionPausedAt?: Date | string | null;
  tierId?: string | null;
};

export type CommunityRevenueTierPricing = {
  monthlyPriceCents?: number | null;
  annualPriceCents?: number | null;
};

export type CommunityRevenueDailyPoint = {
  date: string;
  mrrCents: number;
  arrCents: number;
  paidSubscribers: number;
  churnCount: number;
};

export type CommunityRevenueCohortSummary = {
  cohortMonth: string;
  startingPaid: number;
  retainedPaid: number;
  retentionRate: number;
};

function parseDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function utcDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function endOfUtcDay(dateKey: string): Date {
  return new Date(`${dateKey}T23:59:59.999Z`);
}

function monthKey(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function startOfUtcMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function addUtcMonths(date: Date, months: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
}

function isMembershipPausedOnDay(
  membership: CommunityRevenueMembershipSnapshot,
  dateKey: string,
): boolean {
  const pausedAt = parseDate(membership.subscriptionPausedAt);
  if (!pausedAt) return false;
  return pausedAt.getTime() <= endOfUtcDay(dateKey).getTime();
}

function resolveMembershipPricing(
  membership: CommunityRevenueMembershipSnapshot,
  communityPricing: CommunityMrrPricing,
  tierPricingMap: Record<string, CommunityRevenueTierPricing>,
): CommunityMrrPricing {
  const tier = membership.tierId ? tierPricingMap[membership.tierId] : undefined;
  return {
    monthlyPriceCents: tier?.monthlyPriceCents ?? communityPricing.monthlyPriceCents,
    annualPriceCents: tier?.annualPriceCents ?? communityPricing.annualPriceCents,
  };
}

function wasPaidSubscriberOnDay(
  membership: CommunityRevenueMembershipSnapshot,
  dateKey: string,
): boolean {
  if (!membership.stripeSubscriptionId) return false;
  const joinedAt = parseDate(membership.joinedAt);
  if (!joinedAt || joinedAt.getTime() > endOfUtcDay(dateKey).getTime()) return false;
  const leftAt = parseDate(membership.leftAt);
  if (leftAt && leftAt.getTime() <= endOfUtcDay(dateKey).getTime()) return false;
  if (isMembershipPausedOnDay(membership, dateKey)) return false;
  return true;
}

function isPaidSubscriberRetainedNow(
  membership: CommunityRevenueMembershipSnapshot,
): boolean {
  if (!membership.stripeSubscriptionId) return false;
  if (membership.status !== 'active') return false;
  if (parseDate(membership.leftAt)) return false;
  if (parseDate(membership.subscriptionPausedAt)) return false;
  return true;
}

export function lastNDayKeys(endDate: Date, days: number): string[] {
  const keys: string[] = [];
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const day = new Date(endDate);
    day.setUTCDate(day.getUTCDate() - offset);
    keys.push(utcDateKey(day));
  }
  return keys;
}

export function buildCommunityRevenueDailySeries(
  memberships: CommunityRevenueMembershipSnapshot[],
  dayKeys: string[],
  communityPricing: CommunityMrrPricing,
  tierPricingMap: Record<string, CommunityRevenueTierPricing>,
  todayKey = dayKeys[dayKeys.length - 1] ?? utcDateKey(new Date()),
): CommunityRevenueDailyPoint[] {
  return dayKeys.map((date) => {
    const activeOnDay = memberships.filter((membership) =>
      wasPaidSubscriberOnDay(membership, date),
    );
    const mrrMemberships = activeOnDay.map((membership) => {
      const pricing = resolveMembershipPricing(membership, communityPricing, tierPricingMap);
      return {
        ...membership,
        monthlyPriceCents: pricing.monthlyPriceCents,
        annualPriceCents: pricing.annualPriceCents,
        stripeBillingStatus:
          date === todayKey ? membership.stripeBillingStatus : 'active',
      };
    });
    const mrrCents = calculateCommunityMrrCents(mrrMemberships, communityPricing);
    const churnCount = memberships.filter((membership) => {
      if (!membership.stripeSubscriptionId) return false;
      const leftAt = parseDate(membership.leftAt);
      if (!leftAt) return false;
      return utcDateKey(leftAt) === date;
    }).length;

    return {
      date,
      mrrCents,
      arrCents: mrrCents * 12,
      paidSubscribers: activeOnDay.length,
      churnCount,
    };
  });
}

export function buildCommunityRevenueCohortSummary(
  memberships: CommunityRevenueMembershipSnapshot[],
  now: Date,
  cohortCount = 4,
): CommunityRevenueCohortSummary[] {
  const paidMemberships = memberships.filter((membership) => membership.stripeSubscriptionId);
  const cohorts: CommunityRevenueCohortSummary[] = [];
  const currentMonthStart = startOfUtcMonth(now);

  for (let cohortIndex = cohortCount - 1; cohortIndex >= 0; cohortIndex -= 1) {
    const cohortStart = addUtcMonths(currentMonthStart, -cohortIndex);
    const cohortEnd = addUtcMonths(cohortStart, 1);
    const cohortMembers = paidMemberships.filter((membership) => {
      const joinedAt = parseDate(membership.joinedAt);
      return joinedAt && joinedAt >= cohortStart && joinedAt < cohortEnd;
    });
    const startingPaid = cohortMembers.length;
    if (startingPaid === 0) continue;

    const retainedPaid = cohortMembers.filter(isPaidSubscriberRetainedNow).length;
    cohorts.push({
      cohortMonth: monthKey(cohortStart),
      startingPaid,
      retainedPaid,
      retentionRate: Math.round((retainedPaid / startingPaid) * 1000) / 10,
    });
  }

  return cohorts;
}
